// Écran d'accueil « Aujourd'hui » — rendu DOM.
// La règle de ce qui remonte et dans quel ordre vit dans lib/accueil-logic.js,
// où elle est testée. Ici, uniquement de l'affichage.
//
// Construit en createElement/textContent, jamais en innerHTML : ce module
// affichera bientôt des libellés saisis (notes de frais), et l'idiome sûr doit
// être celui qui est déjà en place quand ça arrivera.

import { construireSignaux, verdictGlobal } from "../lib/accueil-logic.js";
import { eur, nombreFr } from "../lib/format.js";
import { entreeSignal } from "../lib/presse-papier-entrees.js";

const el = (tag, cls, texte) => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (texte != null) n.textContent = texte;
  return n;
};

/**
 * Bandeau de verdict — la réponse au test des cinq secondes.
 * Une phrase, pas un tableau : on doit pouvoir refermer l'app en sachant si
 * quelque chose demande une action.
 */
function rendreVerdict(verdict) {
  const box = el("div", `verdict verdict-${verdict.ton}`);
  const icone = { critique: "⚠️", attention: "•", ok: "✓" }[verdict.ton] ?? "•";
  box.appendChild(el("span", "verdict-ic", icone));
  box.appendChild(el("span", "verdict-txt", verdict.texte));
  return box;
}

/**
 * Une tuile de signal, cliquable vers l'onglet qui porte le détail, avec un
 * bouton « garder » qui la range dans le presse-papier.
 *
 * La tuile EST un `<button>` — on ne peut donc pas y imbriquer le second, ce
 * serait du HTML invalide et les clics se marcheraient dessus. D'où l'hôte
 * `.tuile-hote` qui porte les deux côte à côte, et devient l'élément de grille.
 */
function rendreTuile(signal, allerA, garder) {
  const hote = el("div", "tuile-hote");
  const t = el("button", `tuile tuile-${signal.gravite}`);
  t.type = "button";

  t.appendChild(el("div", "tuile-titre", signal.titre));

  const valeur =
    signal.format === "euros" ? eur(signal.valeur) : String(signal.valeur ?? "—");
  t.appendChild(el("div", "tuile-valeur", valeur));

  if (signal.detail) t.appendChild(el("div", "tuile-detail", signal.detail));

  if (signal.onglet) {
    // Rendre les chiffres cliquables est la moitié du travail : dans l'ancienne
    // app, AUCUN agrégat ne menait à son détail. On voyait un montant, et il
    // fallait aller le rechercher soi-même dans un autre onglet.
    t.addEventListener("click", () => allerA(signal.onglet));
    t.title = "Voir le détail";
  } else {
    t.disabled = true;
  }

  hote.appendChild(t);
  // `garder` est injecté : ce module reste rendable sans presse-papier, ce qui
  // permet de le tester sans monter toute l'interface.
  //
  // La valeur passée est celle AFFICHÉE, pas la brute : la fiche doit dire le
  // même chiffre que l'écran, au caractère près. Un reformatage indépendant
  // finit toujours par diverger, et l'écart est invérifiable après coup.
  if (garder) {
    const entree = entreeSignal(signal, valeur);
    if (entree) hote.appendChild(garder(entree));
  }
  return hote;
}

/** Détail des canaux : une ligne par source, la plus muette en premier. */
function rendreCanaux(sante) {
  const box = el("div", "canaux");
  const canaux = Array.isArray(sante?.canaux) ? sante.canaux : [];
  if (!canaux.length) {
    box.appendChild(el("div", "muted", "Aucun canal connu."));
    return box;
  }
  for (const c of canaux) {
    const ligne = el("div", `canal canal-${c.statut}`);
    ligne.appendChild(el("span", "canal-nom", c.source));

    const etat = {
      arretee: "arrêtée",
      muette: "muette",
      faible: "en baisse",
      ok: "à jour",
    }[c.statut] ?? c.statut;
    ligne.appendChild(el("span", "canal-etat", etat));

    const j = Number(c.jours_silence);
    ligne.appendChild(
      el("span", "canal-silence", Number.isFinite(j) ? `${j} j sans facture` : "—"),
    );

    // La cadence habituelle est ce qui donne l'échelle du problème : un canal à
    // 490 factures/mois qui se tait ne coûte pas la même chose qu'un canal à 2.
    const normale = Number(c.normale_mensuelle);
    if (Number.isFinite(normale) && normale > 0) {
      ligne.appendChild(
        el("span", "canal-cadence", `habituellement ≈ ${nombreFr(normale)} factures/mois`),
      );
    }
    box.appendChild(ligne);
  }
  return box;
}

/**
 * Rend l'écran, dans l'une de ses DEUX vues.
 *
 * `donnees` = { sante, stats, alertes, collecteurs } — chaque champ peut manquer
 * si sa requête a échoué : l'écran se dégrade, il ne disparaît pas.
 *
 * `vue` :
 *   'decisions' (défaut) → onglet « Aujourd'hui », famille Argent.
 *                          Ce qui demande une action : les tuiles cliquables.
 *   'canaux'             → onglet « Canaux », famille Sources.
 *                          L'état de chaque tuyau, canal par canal.
 *
 * POURQUOI ce paramètre existe : les deux onglets rendaient exactement le même
 * écran. Une seule fonction, un seul contenu, deux panneaux de destination — donc
 * deux entrées de navigation qui promettaient deux usages et livraient le même.
 * Le regroupement en familles (Argent / Sources) a rendu la duplication criante :
 * elle était déjà là, elle passait juste inaperçue dans une barre de onze onglets.
 *
 * Le bandeau de verdict reste sur LES DEUX vues, et ce n'est pas un oubli : il dit
 * « les montants affichés ci-dessous sont incomplets ». Sur « Aujourd'hui » c'est
 * la réserve qui rend les chiffres honnêtes, sur « Canaux » c'est le titre de ce
 * qui suit. Le retirer d'« Aujourd'hui » y laisserait des montants faux sans
 * avertissement.
 */
export function rendreAccueil(racine, donnees, allerA, vue = "decisions", garder = null) {
  const signaux = construireSignaux(donnees);
  const verdict = verdictGlobal(signaux);

  const frag = document.createDocumentFragment();
  frag.appendChild(rendreVerdict(verdict));

  if (vue === "canaux") {
    frag.appendChild(el("h2", "sect", "Canaux d'approvisionnement"));
    frag.appendChild(rendreCanaux(donnees?.sante));
  } else {
    const grille = el("div", "tuiles");
    for (const s of signaux) grille.appendChild(rendreTuile(s, allerA, garder));
    frag.appendChild(grille);
  }

  racine.replaceChildren(frag);
}

/**
 * Charge les données de l'accueil.
 *
 * Chaque source est indépendante : une requête qui échoue n'emporte pas les
 * autres. C'est un défaut corrigé de l'ancien écran d'analyse, où une exception
 * sur un panneau EFFAÇAIT ceux qui avaient déjà été rendus.
 */
export async function chargerAccueil(api) {
  const [sante, stats, alertes, collecteurs] = await Promise.all([
    api("sante").catch(() => null),
    api("stats", {}).catch(() => null),
    api("alertes", {}).catch(() => null),
    api("collecteurs_view", { q: "" }).catch(() => null),
  ]);
  return { sante, stats, alertes, collecteurs };
}
